import { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Stack,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Typography,
    LinearProgress,
    Alert,
    useTheme,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
    AreaChart,
    Area,
    Sector,
} from 'recharts';
import MainCard from 'components/ui/Card/MainCard';
import { useGetMetricsFromDdiActionsBasedOnProjectAndTimeIntervalMetaMetricsGetQuery } from 'store/metadata/metadataApi';
import { formatBytes, isByteMetric } from 'utils/format';
import { formatDateCompact, convertLocalDateToUTC, formatDateTime } from 'utils/date';
import { blue, green, amber, deepOrange, deepPurple, lightGreen, yellow, orange } from '@mui/material/colors';

interface MetricBucket {
    key: string;
    doc_count: number;
    total_size_agg?: {
        value: number;
    };
    status_code_agg?: {
        buckets: {
            key: string;
            doc_count: number;
        }[];
    };
}

interface TimeHistogramBucket {
    key_as_string: string;
    key: number;
    doc_count: number;
    total_size_agg?: {
        value: number;
    };
    action_type_agg?: {
        doc_count_error_upper_bound: number;
        sum_other_doc_count: number;
        buckets: MetricBucket[];
    };
    status_code_agg?: {
        doc_count_error_upper_bound: number;
        sum_other_doc_count: number;
        buckets: {
            key: string;
            doc_count: number;
        }[];
    };
}

interface HistogramMetricsResponse {
    time_histogram_agg: {
        buckets: TimeHistogramBucket[];
    };
}

interface AggregateMetricsResponse {
    action_type_agg?: {
        buckets: MetricBucket[];
    };
    status_code_agg?: {
        buckets: MetricBucket[];
    };
}

interface TableMetricsResponse {
    total: number;
    buckets: Array<{
        key: {
            preferred_username?: string;
            title?: string;
            [key: string]: any;
        };
        doc_count: number;
    }>;
}

type MetricsResponse = HistogramMetricsResponse | AggregateMetricsResponse | TableMetricsResponse;

type ChartData =
    | {
          type: 'table';
          data: TableMetricsResponse;
      }
    | {
          type: 'histogram';
          data: any[];
          keys: string[];
      }
    | {
          type: 'aggregate';
          data: { name: string; value: number }[];
      };

interface MetricChartProps {
    metricType: string;
    project: string;
    startTime: Date;
    endTime: Date;
    timeInterval: string;
    chartIndex: number;
    initialChartType?: string;
    onChartTypeChange?: (chartType: string) => void;
}

const MetricChart = ({
    metricType,
    project,
    startTime,
    endTime,
    timeInterval,
    initialChartType,
    onChartTypeChange,
}: MetricChartProps) => {
    const theme = useTheme();
    const [selectedChartType, setSelectedChartType] = useState(initialChartType || '');
    const [activeIndex, setActiveIndex] = useState(0);

    const onPieEnter = (_: any, index: number) => {
        setActiveIndex(index);
    };

    useEffect(() => {
        if (initialChartType && initialChartType !== selectedChartType) {
            setSelectedChartType(initialChartType);
        }
    }, [initialChartType]);

    const COLORS = [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
        theme.palette.info.main,
        blue[300],
        green[300],
        amber[300],
        deepOrange[300],
        deepPurple[300],
        lightGreen[300],
        yellow[300],
        orange[300],
    ];

    const isTableMetric = metricType.includes('-table');

    const {
        data: metricsData,
        isLoading: metricsLoading,
        error: metricsError,
    } = useGetMetricsFromDdiActionsBasedOnProjectAndTimeIntervalMetaMetricsGetQuery({
        metricType: metricType,
        project: project,
        startTime: (convertLocalDateToUTC(startTime) ?? '').replace('T', ' ').substring(0, 19),
        endTime: (convertLocalDateToUTC(endTime) ?? '').replace('T', ' ').substring(0, 19),
        timeInterval: timeInterval || undefined,
        start: isTableMetric ? 0 : undefined,
        size: isTableMetric ? 1000 : undefined,
    });

    const chartData: ChartData | null = useMemo(() => {
        if (!metricsData) return null;
        if (typeof metricsData !== 'object' || metricsData === null) return null;

        const data = metricsData as MetricsResponse;

        try {
            if (isTableMetric && 'total' in data && 'buckets' in data) {
                return { type: 'table', data: data as TableMetricsResponse };
            }
            if (metricType === 'status-codes' && ('action_type_agg' in data || 'status_code_agg' in data)) {
                const aggregateData = data as AggregateMetricsResponse;

                if (aggregateData.status_code_agg?.buckets) {
                    return {
                        type: 'aggregate',
                        data: aggregateData.status_code_agg.buckets.map(bucket => ({
                            name: bucket.key,
                            value: bucket.doc_count,
                        })),
                    };
                }

                if (aggregateData.action_type_agg?.buckets) {
                    const isMetricForBytes = isByteMetric(metricType);

                    return {
                        type: 'aggregate',
                        data: aggregateData.action_type_agg.buckets.map(bucket => ({
                            name: bucket.key,
                            value: isMetricForBytes
                                ? (bucket as any).total_size_agg?.value ?? 0
                                : (bucket as any).total_size_agg?.value ?? bucket.doc_count,
                        })),
                    };
                }
            }
            if ('time_histogram_agg' in data) {
                const histogramData = data as HistogramMetricsResponse;
                if (!histogramData.time_histogram_agg?.buckets) return null;

                if (metricType === 'status-codes') {
                    const statusCodeTotals = new Map<string, number>();

                    histogramData.time_histogram_agg.buckets.forEach(bucket => {
                        if ((bucket as any).status_code_agg?.buckets) {
                            (bucket as any).status_code_agg.buckets.forEach((statusBucket: any) => {
                                const key = statusBucket.key;
                                const value = statusBucket.doc_count;
                                statusCodeTotals.set(key, (statusCodeTotals.get(key) || 0) + value);
                            });
                        }
                    });

                    if (statusCodeTotals.size > 0) {
                        return {
                            type: 'aggregate',
                            data: Array.from(statusCodeTotals.entries()).map(([name, value]) => ({
                                name,
                                value,
                            })),
                        };
                    }
                }

                const allKeys = new Set<string>();
                let hasAggregations = false;

                histogramData.time_histogram_agg.buckets.forEach(bucket => {
                    if (bucket.action_type_agg) {
                        hasAggregations = true;
                        if (bucket.action_type_agg.buckets && Array.isArray(bucket.action_type_agg.buckets)) {
                            bucket.action_type_agg.buckets.forEach(actionBucket => {
                                if (actionBucket && actionBucket.key) {
                                    allKeys.add(actionBucket.key);
                                }
                            });
                        }
                    }

                    if ((bucket as any).status_code_agg) {
                        hasAggregations = true;
                        if (
                            (bucket as any).status_code_agg.buckets &&
                            Array.isArray((bucket as any).status_code_agg.buckets)
                        ) {
                            (bucket as any).status_code_agg.buckets.forEach((statusBucket: any) => {
                                if (statusBucket && statusBucket.key) {
                                    allKeys.add(statusBucket.key);
                                }
                            });
                        }
                    }
                });

                const processedData = histogramData.time_histogram_agg.buckets.map(bucket => {
                    const isMetricForBytes = isByteMetric(metricType);

                    const totalValue = isMetricForBytes
                        ? (bucket as any).total_size_agg?.value ?? 0
                        : (bucket as any).total_size_agg?.value ?? bucket.doc_count;

                    const isDataTransferMetric =
                        metricType === 'deleted-data' ||
                        metricType === 'uploaded-data' ||
                        metricType === 'downloaded-data';

                    const result: any = {
                        date: formatDateTime(bucket.key_as_string),
                    };

                    if (!isDataTransferMetric || !hasAggregations) {
                        result.total = totalValue;
                    }

                    if (!hasAggregations) {
                        return result;
                    }

                    allKeys.forEach(key => {
                        let value = 0;

                        const actionBucket = bucket.action_type_agg?.buckets?.find(b => b.key === key);
                        if (actionBucket) {
                            if (isMetricForBytes) {
                                value = (actionBucket as any).total_size_agg?.value ?? 0;
                            } else {
                                value = (actionBucket as any).total_size_agg?.value ?? actionBucket.doc_count;
                            }
                        } else {
                            const statusBucket = (bucket as any).status_code_agg?.buckets?.find(
                                (b: any) => b.key === key
                            );
                            if (statusBucket) {
                                if (isMetricForBytes) {
                                    value = (statusBucket as any).total_size_agg?.value ?? 0;
                                } else {
                                    value = (statusBucket as any).total_size_agg?.value ?? statusBucket.doc_count;
                                }
                            }
                        }

                        result[key] = value;
                    });

                    return result;
                });

                return {
                    type: 'histogram',
                    data: processedData,
                    keys: Array.from(allKeys),
                };
            }
            if ('action_type_agg' in data || 'status_code_agg' in data) {
                const aggregateData = data as AggregateMetricsResponse;

                if (aggregateData.status_code_agg?.buckets) {
                    return {
                        type: 'aggregate',
                        data: aggregateData.status_code_agg.buckets.map(bucket => ({
                            name: bucket.key,
                            value: bucket.doc_count,
                        })),
                    };
                }

                if (aggregateData.action_type_agg?.buckets) {
                    const isMetricForBytes = isByteMetric(metricType);

                    return {
                        type: 'aggregate',
                        data: aggregateData.action_type_agg.buckets.map(bucket => ({
                            name: bucket.key,
                            value: isMetricForBytes
                                ? (bucket as any).total_size_agg?.value ?? 0
                                : (bucket as any).total_size_agg?.value ?? bucket.doc_count,
                        })),
                    };
                }

                return null;
            }
            return null;
        } catch (error) {
            return null;
        }
    }, [metricsData, isTableMetric]);

    useEffect(() => {
        if (chartData) {
            if (chartData.type === 'histogram' && !['bar', 'line', 'area'].includes(selectedChartType)) {
                setSelectedChartType('bar');
            } else if (chartData.type === 'aggregate' && !['pie', 'bar'].includes(selectedChartType)) {
                const isStatusCodeMetric = metricType === 'status-codes';
                setSelectedChartType(isStatusCodeMetric ? 'pie' : 'bar');
            } else if (chartData.type === 'table' && !['table', 'pie', 'bar'].includes(selectedChartType)) {
                setSelectedChartType('table');
            }
        }
    }, [chartData, selectedChartType, metricType]);

    const renderActiveShape = (props: any) => {
        const RADIAN = Math.PI / 180;
        const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } =
            props;
        const sin = Math.sin(-RADIAN * midAngle);
        const cos = Math.cos(-RADIAN * midAngle);
        const sx = cx + (outerRadius + 10) * cos;
        const sy = cy + (outerRadius + 10) * sin;
        const mx = cx + (outerRadius + 30) * cos;
        const my = cy + (outerRadius + 30) * sin;
        const ex = mx + (cos >= 0 ? 1 : -1) * 22;
        const ey = my;
        const textAnchor = cos >= 0 ? 'start' : 'end';
        const formattedValue = isByteMetric(metricType) ? formatBytes(value) : value;

        return (
            <g>
                <Sector
                    cx={cx}
                    cy={cy}
                    innerRadius={innerRadius}
                    outerRadius={outerRadius}
                    startAngle={startAngle}
                    endAngle={endAngle}
                    fill={fill}
                />
                <Sector
                    cx={cx}
                    cy={cy}
                    startAngle={startAngle}
                    endAngle={endAngle}
                    innerRadius={outerRadius + 6}
                    outerRadius={outerRadius + 10}
                    fill={fill}
                />
                <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill='none' />
                <circle cx={ex} cy={ey} r={2} fill={fill} stroke='none' />
                <text
                    x={ex + (cos >= 0 ? 1 : -1) * 12}
                    y={ey}
                    dy={-18}
                    textAnchor={textAnchor}
                    fill={theme.palette.text.primary}
                    fontWeight='bold'>
                    {payload.name}
                </text>
                <text
                    x={ex + (cos >= 0 ? 1 : -1) * 12}
                    y={ey}
                    dy={0}
                    textAnchor={textAnchor}
                    fill={theme.palette.text.primary}>
                    {`Value: ${formattedValue}`}
                </text>
                <text
                    x={ex + (cos >= 0 ? 1 : -1) * 12}
                    y={ey}
                    dy={18}
                    textAnchor={textAnchor}
                    fill={theme.palette.text.secondary}>
                    {`(Rate ${(percent * 100).toFixed(1)}%)`}
                </text>
            </g>
        );
    };

    const renderChartSelector = () => {
        if (!chartData) return null;

        let options: string[] = [];
        if (chartData.type === 'histogram') {
            options = ['bar', 'line', 'area'];
        } else if (chartData.type === 'aggregate') {
            options = ['pie', 'bar'];
        } else if (chartData.type === 'table') {
            options = ['table', 'pie', 'bar'];
        }

        if (options.length <= 1) return null;

        const validChartType = options.includes(selectedChartType) ? selectedChartType : options[0];

        return (
            <FormControl size='small' sx={{ minWidth: 120 }}>
                <InputLabel>View As</InputLabel>
                <Select
                    value={validChartType}
                    label='View As'
                    onChange={e => {
                        const newChartType = e.target.value as string;
                        setSelectedChartType(newChartType);
                        onChartTypeChange?.(newChartType);
                    }}>
                    {options.map(opt => (
                        <MenuItem key={opt} value={opt}>
                            {opt.charAt(0).toUpperCase() + opt.slice(1)}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    };

    const CHART_HEIGHT = 400;

    const renderChart = () => {
        if (!chartData) {
            return <Alert severity='info'>No data available for the selected metric type and time range.</Alert>;
        }

        if (
            (chartData.type === 'table' && chartData.data.buckets.length === 0) ||
            (chartData.type !== 'table' && Array.isArray(chartData.data) && chartData.data.length === 0)
        ) {
            return <Alert severity='info'>No data available for the selected metric type and time range.</Alert>;
        }

        switch (chartData.type) {
            case 'histogram': {
                const histogramData = Array.isArray(chartData.data) ? chartData.data : [];

                if (selectedChartType === 'line') {
                    return (
                        <ResponsiveContainer width='100%' height={CHART_HEIGHT}>
                            <LineChart data={histogramData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray='3 3' />
                                <XAxis dataKey='date' />
                                <YAxis
                                    tickFormatter={isByteMetric(metricType) ? formatBytes : undefined}
                                    domain={isByteMetric(metricType) ? ['dataMin', 'dataMax'] : undefined}
                                />
                                <RechartsTooltip
                                    formatter={(value: number) =>
                                        isByteMetric(metricType) ? formatBytes(value) : value
                                    }
                                />
                                <Legend />
                                {histogramData.length > 0 && histogramData[0].total !== undefined && (
                                    <Line
                                        type='monotone'
                                        dataKey='total'
                                        stroke={theme.palette.primary.main}
                                        name='Total'
                                    />
                                )}
                                {Array.isArray(chartData.keys) &&
                                    chartData.keys.map((key: string, index: number) => (
                                        <Line
                                            key={key}
                                            type='monotone'
                                            dataKey={key}
                                            stroke={COLORS[index % COLORS.length]}
                                            name={key.replace(/_/g, ' ')}
                                        />
                                    ))}
                            </LineChart>
                        </ResponsiveContainer>
                    );
                } else if (selectedChartType === 'area') {
                    return (
                        <ResponsiveContainer width='100%' height={CHART_HEIGHT}>
                            <AreaChart data={histogramData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray='3 3' />
                                <XAxis dataKey='date' />
                                <YAxis
                                    tickFormatter={isByteMetric(metricType) ? formatBytes : undefined}
                                    domain={isByteMetric(metricType) ? ['dataMin', 'dataMax'] : undefined}
                                />
                                <RechartsTooltip
                                    formatter={(value: number) =>
                                        isByteMetric(metricType) ? formatBytes(value) : value
                                    }
                                />
                                <Legend />
                                {histogramData.length > 0 && histogramData[0].total !== undefined && (
                                    <Area
                                        type='monotone'
                                        dataKey='total'
                                        stroke={theme.palette.primary.main}
                                        fill={theme.palette.primary.light}
                                        name='Total'
                                    />
                                )}
                                {Array.isArray(chartData.keys) &&
                                    chartData.keys.map((key: string, index: number) => (
                                        <Area
                                            key={key}
                                            type='monotone'
                                            dataKey={key}
                                            stroke={COLORS[index % COLORS.length]}
                                            fill={COLORS[index % COLORS.length]}
                                            name={key.replace(/_/g, ' ')}
                                            fillOpacity={0.7}
                                        />
                                    ))}
                            </AreaChart>
                        </ResponsiveContainer>
                    );
                }
                return (
                    <ResponsiveContainer width='100%' height={CHART_HEIGHT}>
                        <BarChart data={histogramData}>
                            <CartesianGrid strokeDasharray='3 3' />
                            <XAxis dataKey='date' />
                            <YAxis
                                tickFormatter={isByteMetric(metricType) ? formatBytes : undefined}
                                domain={isByteMetric(metricType) ? ['dataMin', 'dataMax'] : undefined}
                            />
                            <RechartsTooltip
                                formatter={(value: number) => (isByteMetric(metricType) ? formatBytes(value) : value)}
                            />
                            <Legend />
                            {histogramData.length > 0 && histogramData[0].total !== undefined && (
                                <Bar dataKey='total' fill={theme.palette.primary.main} name='Total' />
                            )}
                            {Array.isArray(chartData.keys) &&
                                chartData.keys.map((key: string, index: number) => (
                                    <Bar
                                        key={key}
                                        dataKey={key}
                                        fill={COLORS[index % COLORS.length]}
                                        name={key.replace(/_/g, ' ')}
                                    />
                                ))}
                        </BarChart>
                    </ResponsiveContainer>
                );
            }

            case 'aggregate': {
                const aggregateData = Array.isArray(chartData.data) ? chartData.data : [];
                const cleanAggregateData = aggregateData.filter((d: any) => d.value > 0);

                const customLegendPayload = aggregateData.map((entry: any, index: number) => ({
                    value: entry.name,
                    type: 'square' as const,
                    id: entry.name,
                    color: COLORS[index % COLORS.length],
                }));

                if (selectedChartType === 'bar') {
                    return (
                        <ResponsiveContainer width='100%' height={CHART_HEIGHT}>
                            <BarChart data={aggregateData}>
                                <CartesianGrid strokeDasharray='3 3' />
                                <XAxis dataKey='name' />
                                <YAxis
                                    tickFormatter={isByteMetric(metricType) ? formatBytes : undefined}
                                    domain={isByteMetric(metricType) ? ['dataMin', 'dataMax'] : undefined}
                                />
                                <RechartsTooltip
                                    formatter={(value: number) =>
                                        isByteMetric(metricType) ? formatBytes(value) : value
                                    }
                                />
                                <Legend payload={customLegendPayload} />
                                <Bar dataKey='value'>
                                    {aggregateData.map((entry: any, index: number) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    );
                }
                return (
                    <ResponsiveContainer width='100%' height={CHART_HEIGHT}>
                        <PieChart margin={{ top: 20, left: 20, right: 20, bottom: 20 }}>
                            <Pie
                                activeIndex={activeIndex}
                                activeShape={renderActiveShape}
                                data={cleanAggregateData}
                                cx='50%'
                                cy='50%'
                                innerRadius='50%'
                                outerRadius='60%'
                                fill={theme.palette.primary.main}
                                dataKey='value'
                                onMouseEnter={onPieEnter}>
                                {cleanAggregateData.map((entry: any, index: number) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                );
            }

            case 'table': {
                const metricBuckets = chartData.data.buckets;
                const firstBucket = metricBuckets[0]?.key;
                const keyName =
                    firstBucket && 'title' in firstBucket
                        ? 'Title'
                        : firstBucket && 'preferred_username' in firstBucket
                        ? 'Username'
                        : 'Name';

                if (selectedChartType === 'table') {
                    return (
                        <TableContainer component={Paper} sx={{ maxHeight: CHART_HEIGHT }}>
                            <Table stickyHeader aria-label='metric table'>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{keyName}</TableCell>
                                        <TableCell align='right'>Count</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {metricBuckets.map((row, index) => (
                                        <TableRow key={index}>
                                            <TableCell component='th' scope='row'>
                                                {row.key.title || row.key.preferred_username || 'Unknown'}
                                            </TableCell>
                                            <TableCell align='right'>{row.doc_count}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    );
                }

                const chartFormattedData = metricBuckets.map(row => ({
                    name: row.key.title || row.key.preferred_username || 'Unknown',
                    value: row.doc_count,
                }));

                if (selectedChartType === 'bar') {
                    return (
                        <ResponsiveContainer width='100%' height={CHART_HEIGHT}>
                            <BarChart data={chartFormattedData} layout='vertical' margin={{ right: 20, left: 80 }}>
                                <CartesianGrid strokeDasharray='3 3' />
                                <XAxis type='number' />
                                <YAxis type='category' dataKey='name' width={150} />
                                <RechartsTooltip />
                                <Legend />
                                <Bar dataKey='value' fill={theme.palette.primary.main} />
                            </BarChart>
                        </ResponsiveContainer>
                    );
                }

                const cleanChartFormattedData = chartFormattedData.filter((d: any) => d.value > 0);
                return (
                    <ResponsiveContainer width='100%' height={CHART_HEIGHT}>
                        <PieChart margin={{ top: 20, left: 20, right: 20, bottom: 20 }}>
                            <Pie
                                activeIndex={activeIndex}
                                activeShape={renderActiveShape}
                                data={cleanChartFormattedData}
                                cx='50%'
                                cy='50%'
                                innerRadius='50%'
                                outerRadius='60%'
                                fill={theme.palette.primary.main}
                                dataKey='value'
                                nameKey='name'
                                onMouseEnter={onPieEnter}>
                                {cleanChartFormattedData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                );
            }
            default:
                return null;
        }
    };

    return (
        <Box sx={{ width: { xs: '100%', md: `calc(50% - ${theme.spacing(1.5)})` }, display: 'flex' }}>
            <MainCard
                sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}
                contentSX={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                title={`${metricType.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Metrics`}
                secondary={
                    <Stack direction='row' spacing={2} alignItems='center'>
                        {renderChartSelector()}
                        <Typography variant='caption' noWrap>
                            from {formatDateCompact(convertLocalDateToUTC(startTime) ?? '')} to{' '}
                            {formatDateCompact(convertLocalDateToUTC(endTime) ?? '')}
                            {timeInterval && ` (${timeInterval} intervals)`}
                        </Typography>
                    </Stack>
                }>
                <Box sx={{ flex: 1 }}>
                    {!timeInterval && (
                        <Alert severity='info' sx={{ mb: 2 }}>
                            This chart shows a total summary for the selected period and does not use a time interval.
                        </Alert>
                    )}
                    {metricsLoading && <LinearProgress />}
                    {metricsError && <Alert severity='error'>Error loading metrics data.</Alert>}
                    {!metricsLoading && !metricsError && renderChart()}
                </Box>
            </MainCard>
        </Box>
    );
};

export default MetricChart;
